import React, { useState } from 'react';
import { User, BookOpen, Activity, Github, Briefcase, Award, FileText, GraduationCap, Mail, Phone, ArrowLeft, Pencil } from 'lucide-react';
import api from '../api';
import FormattedResumeSummary from './FormattedResumeSummary';

const parseJsonSafe = (value, defaultValue = []) => {
  if (!value) return defaultValue;
  if (Array.isArray(value)) return value;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return defaultValue;
  }
};

const getFileUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const base = api.defaults.baseURL ? api.defaults.baseURL.replace(/\/$/, '') : '';
  return url.startsWith('/') ? `${base}${url}` : `${base}/${url}`;
};

const getText = (item, keys, fallback = 'Not specified') => {
  if (!item) return fallback;
  if (typeof item === 'string') return item;
  for (const key of keys) {
    if (item[key]) return item[key];
  }
  return fallback;
};

const getScore = (item) => {
  const value = item?.score ?? item?.skill_score ?? item?.rating ?? item?.level_score;
  if (value === null || value === undefined || value === '') return '-- / 10';
  if (String(value).includes('/')) return String(value).replace(/\s*\/\s*/g, ' / ');
  return `${value} / 10`;
};

const getKnowledgeScore = (student, item) => {
  if (student?.ai_knowledge_scores) {
    const kScores = parseJsonSafe(student.ai_knowledge_scores, []);
    const matched = kScores.find(k => k.name?.toLowerCase() === item?.name?.toLowerCase());
    if (matched) {
      const val = matched.score;
      if (val === null || val === undefined || val === '') return '-- / 10';
      if (String(val).includes('/')) return String(val).replace(/\s*\/\s*/g, ' / ');
      return `${val} / 10`;
    }
  }
  const value = item?.knowledge_score;
  if (value === null || value === undefined || value === '') return '-- / 10';
  if (String(value).includes('/')) return String(value).replace(/\s*\/\s*/g, ' / ');
  return `${value} / 10`;
};

const renderChips = (items, fallback = 'Not added') => {
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  if (list.length === 0) {
    return <span style={{ color: 'var(--text-faint)', fontSize: '13px' }}>{fallback}</span>;
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
      {list.map((item, index) => (
        <span key={index} className="uni-profile-badge" style={{ padding: '5px 10px', borderRadius: '18px', fontSize: '12px', fontWeight: 700 }}>
          {typeof item === 'string' ? item : item.name || item.title || item.label || 'Item'}
        </span>
      ))}
    </div>
  );
};

const renderScoreTable = (student, items, label, fallback, isSkill = true) => {
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) {
    return <div style={{ color: 'var(--text-faint)', padding: '38px', textAlign: 'center', border: '1px solid var(--border)', borderRadius: '10px' }}>{fallback}</div>;
  }

  const displayItems = list.slice(0, isSkill ? 8 : 14);

  const getLevelPct = (item, idx) => {
    if (item?.level) {
      const parsed = parseFloat(item.level);
      if (!isNaN(parsed)) return parsed > 1 ? parsed / 100 : parsed;
    }
    if (item?.score) {
      const parsed = parseFloat(String(item.score).split('/')[0]);
      if (!isNaN(parsed)) return parsed > 1 ? parsed / 10 : parsed;
    }
    return isSkill ? (0.85 - (idx * 0.04)) : (0.8 - (idx * 0.03));
  };

  const axes = [0, 45, 90, 135, 180, 225, 270, 315];
  const R = 110;
  const toXY = (angleDeg, r) => ({
    x: r * Math.sin((angleDeg * Math.PI) / 180),
    y: -r * Math.cos((angleDeg * Math.PI) / 180)
  });

  const dataPoints = displayItems.slice(0, 8).map((t, i) => toXY(axes[i % 8], R * getLevelPct(t, i)));
  const dataPolygon = dataPoints.map(p => `${p.x},${p.y}`).join(' ');
  const gridPolygon = axes.map(a => `${toXY(a, R).x},${toXY(a, R).y}`).join(' ');

  const primaryColor = isSkill ? '#10b981' : '#3b82f6';
  const polygonFill = isSkill ? 'rgba(16, 185, 129, 0.18)' : 'rgba(59, 130, 246, 0.18)';
  const outerScaleFill = isSkill ? 'rgba(16, 185, 129, 0.04)' : 'rgba(59, 130, 246, 0.04)';

  return (
    <div className="stu-fade-in" style={{ padding: '10px 0' }}>
      <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--proto-white)', textAlign: 'center', marginBottom: '32px', letterSpacing: '-0.01em' }}>
        {isSkill ? 'AI-Analyzed Skill Categories' : 'AI-Detected Tools & Frameworks'}
      </h3>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '40px', alignItems: 'center' }}>
        {/* Left Column: Radar Chart */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '340px', position: 'relative' }}>
          <svg width="340" height="340" viewBox="-170 -170 340 340" style={{ overflow: 'visible' }}>
            {[0.25, 0.5, 0.75, 1.0].map((scale, i) => (
              <polygon
                key={i}
                points={gridPolygon}
                transform={`scale(${scale})`}
                fill={i === 3 ? outerScaleFill : 'none'}
                stroke="#cbd5e1"
                strokeWidth="1"
                strokeDasharray={i === 3 ? '0' : '3,3'}
              />
            ))}
            {axes.map((deg, i) => {
              const end = toXY(deg, R);
              return <line key={i} x1="0" y1="0" x2={end.x} y2={end.y} stroke="#cbd5e1" strokeWidth="1" />;
            })}
            <polygon points={dataPolygon} fill={polygonFill} stroke={primaryColor} strokeWidth="2.5" />
            {dataPoints.map((p, i) => (
              <circle
                key={i}
                cx={p.x}
                cy={p.y}
                r="4.5"
                fill={displayItems[i]?.color || primaryColor}
                stroke="#ffffff"
                strokeWidth="1.5"
              />
            ))}
            {displayItems.slice(0, 8).map((t, i) => {
              const lp = toXY(axes[i % 8], R + 22);
              const anchor = lp.x > 5 ? 'start' : lp.x < -5 ? 'end' : 'middle';
              const itemName = getText(t, ['name', 'title', 'category', 'tool', 'framework']);
              return (
                <text key={i} x={lp.x} y={lp.y + 4} textAnchor={anchor} fill="var(--proto-white)" fontSize="11" fontWeight="600">
                  {itemName.length > 14 ? itemName.substring(0, 13) + '…' : itemName}
                </text>
              );
            })}
          </svg>
        </div>

        {/* Right Column: Scrollable List / Table */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '460px', paddingRight: '4px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', overflowY: 'auto', flex: 1, paddingRight: '4px', position: 'relative' }}>
            {/* Table Header Row */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingBottom: '8px',
              borderBottom: '1.5px solid var(--border)',
              marginBottom: '4px',
              position: 'sticky',
              top: 0,
              background: 'var(--bg-body)',
              zIndex: 10
            }}>
              <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {label}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', width: '110px', textAlign: 'center', letterSpacing: '0.05em', lineHeight: '1.4' }}>
                  SKILL<br />SCORE
                </span>
                <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', width: '90px', textAlign: 'center', letterSpacing: '0.05em' }}>
                  KNOWLEDGE SCORE
                </span>
              </div>
            </div>

            {displayItems.map((item, idx) => {
              const itemName = getText(item, ['name', 'title', 'category', 'tool', 'framework']);
              const skillScoreStr = getScore(item);
              const knowledgeScoreStr = getKnowledgeScore(student, item);

              let kNum = null;
              if (knowledgeScoreStr && knowledgeScoreStr !== '-- / 10') {
                const parsed = parseFloat(knowledgeScoreStr.split('/')[0].trim());
                if (!isNaN(parsed)) kNum = parsed;
              }

              const kColor = kNum !== null
                ? (kNum >= 7 ? '#16a34a' : kNum >= 5 ? '#ea580c' : '#dc2626')
                : 'var(--text-muted)';
              const kBg = kNum !== null
                ? (kNum >= 7 ? 'rgba(34,197,94,0.10)' : kNum >= 5 ? 'rgba(251,146,60,0.10)' : 'rgba(248,113,113,0.10)')
                : 'var(--bg-body)';
              const kBorder = kNum !== null
                ? (kNum >= 7 ? '#bbf7d0' : kNum >= 5 ? '#fed7aa' : '#fecaca')
                : 'var(--border)';

              return (
                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '10px', borderBottom: '1px solid var(--border)', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 1, minWidth: 0 }}>
                    <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: item?.color || primaryColor, flexShrink: 0, display: 'inline-block' }}></span>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--proto-white)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {itemName}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }}>
                    {/* Skill Score */}
                    <div style={{ width: '110px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: '12px', color: 'var(--proto-white)', fontWeight: '600', borderRadius: '16px', border: '1px solid var(--border)', padding: '4px 14px' }}>
                        {skillScoreStr}
                      </span>
                    </div>
                    {/* Knowledge Score */}
                    <div style={{ width: '90px', display: 'flex', justifyContent: 'center' }}>
                      <span
                        style={{
                          background: kBg,
                          color: kColor,
                          padding: '4px 14px',
                          borderRadius: '16px',
                          fontSize: '12px',
                          fontWeight: '700',
                          border: `1px solid ${kBorder}`,
                          width: '100%',
                          textAlign: 'center',
                          cursor: 'default'
                        }}>
                        {knowledgeScoreStr}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const renderCards = (items, config) => {
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) {
    return <div style={{ color: 'var(--text-faint)', padding: '38px', textAlign: 'center', border: '1px solid var(--border)', borderRadius: '10px' }}>{config.empty}</div>;
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
      {list.map((item, index) => (
        <div key={index} style={{ border: '1px solid var(--border)', borderRadius: '12px', background: 'var(--bg-body)', padding: '20px', transition: 'transform 0.2s, box-shadow 0.2s', boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
            <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: 'var(--cyan-dim)', color: 'var(--cyan)', display: 'grid', placeItems: 'center', flex: '0 0 auto' }}>
              {config.icon}
            </div>
            <div style={{ minWidth: 0 }}>
              <h4 style={{ margin: 0, color: 'var(--proto-white)', fontSize: '16px', fontWeight: 800, lineHeight: 1.35 }}>{config.title(item)}</h4>
              {config.subtitle(item) && <p style={{ margin: '6px 0 0', color: 'var(--cyan)', fontWeight: 700, fontSize: '13px' }}>{config.subtitle(item)}</p>}
            </div>
          </div>
          {config.description(item) && <p style={{ color: 'var(--text-muted)', lineHeight: 1.55, margin: '14px 0 0', fontSize: '14px' }}>{config.description(item)}</p>}
          {config.meta(item) && <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px solid rgba(150, 150, 150, 0.1)', color: 'var(--text-faint)', fontSize: '12px', fontWeight: 700, display: 'flex', gap: '6px', alignItems: 'center' }}>
            {config.meta(item)}
          </div>}
        </div>
      ))}
    </div>
  );
};

const tableHeadStyle = {
  padding: '14px 16px',
  textAlign: 'left',
  color: 'var(--text-muted)',
  fontSize: '12px',
  textTransform: 'uppercase',
  fontWeight: 800
};

const pillStyle = {
  border: '1px solid var(--border)',
  borderRadius: '16px',
  padding: '5px 12px',
  fontWeight: 700
};

const sidebarLineStyle = {
  color: 'var(--text-muted)',
  fontSize: '13px',
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  minWidth: 0
};

const smallLabelStyle = {
  color: 'var(--text-muted)',
  fontWeight: 700,
  fontSize: '13px',
  marginBottom: '8px'
};

const InfoPanel = ({ title, icon, children }) => (
  <div style={{ 
    border: '1px solid var(--border)', 
    borderRadius: '16px', 
    padding: '24px', 
    background: 'var(--bg-body)', 
    boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    position: 'relative',
    overflow: 'hidden'
  }}>
    <div style={{ position: 'absolute', top: 0, left: 0, width: '4px', height: '100%', background: 'var(--cyan)' }} />
    <h4 style={{ 
      color: 'var(--proto-white)', 
      margin: '0 0 20px', 
      display: 'flex', 
      gap: '10px', 
      alignItems: 'center', 
      fontSize: '16px', 
      fontWeight: 800, 
      borderBottom: '1px solid var(--border)', 
      paddingBottom: '12px' 
    }}>
      <div style={{ color: 'var(--cyan)', display: 'flex', alignItems: 'center' }}>
        {icon}
      </div>
      {title}
    </h4>
    <div style={{ display: 'grid', gap: '12px' }}>
      {children}
    </div>
  </div>
);

const InfoRow = ({ label, value }) => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'flex-start', 
    fontSize: '14px', 
    padding: '8px 0',
    borderBottom: '1px solid rgba(150, 150, 150, 0.05)'
  }}>
    <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{label}</span>
    <span style={{ color: 'var(--proto-white)', fontWeight: 700, textAlign: 'right', maxWidth: '60%' }}>{value}</span>
  </div>
);

const Stat = ({ value, label }) => (
  <div style={{ 
    background: 'var(--bg-body)', 
    border: '1px solid var(--border)', 
    borderRadius: '12px', 
    padding: '16px 12px', 
    textAlign: 'center', 
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
  }}>
    <strong style={{ display: 'block', fontSize: '22px', color: 'var(--cyan)', marginBottom: '6px', fontWeight: 800 }}>{value}</strong>
    <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px' }}>{label}</span>
  </div>
);

const StudentProfileDetail = ({ student, onBack, coursesData = [], loadingCourses = false, canEdit = false, onEditProfile }) => {
  const [activeSection, setActiveSection] = useState('details');

  if (!student) return null;

  const domains = parseJsonSafe(student.preferred_domains, student.primary_domain ? [student.primary_domain] : []);
  const skills = student.skills_list?.length
    ? student.skills_list
    : String(student.skills || '').split(',').map(skill => skill.trim()).filter(Boolean);
  const toolset = parseJsonSafe(student.ai_toolset, []);
  const skillset = parseJsonSafe(student.ai_skillset, []);
  const projects = parseJsonSafe(student.github_projects, []);
  const experiences = parseJsonSafe(student.experiences, []);
  const awards = parseJsonSafe(student.certificates, []);
  const events = parseJsonSafe(student.events, []);
  const marksCards = parseJsonSafe(student.marks_cards, []);
  const resumeUrl = getFileUrl(student.resume_url);

  const tabs = [
    { id: 'details', label: 'Overview', icon: <User size={15} /> },
    { id: 'resume', label: 'AI Resume Summary', icon: <FileText size={15} /> },
    { id: 'courses', label: 'Courses', icon: <BookOpen size={15} /> },
    { id: 'skillset', label: 'Skills', icon: <Activity size={15} /> },
    { id: 'toolset', label: 'Tools', icon: <BookOpen size={15} /> },
    { id: 'projects', label: 'Tasks', icon: <Github size={15} /> },
    { id: 'experience', label: 'Experience', icon: <Briefcase size={15} /> },
    { id: 'awards', label: 'Awards', icon: <Award size={15} /> },
    { id: 'marks', label: 'Marks Cards', icon: <FileText size={15} /> }
  ];

  const renderSection = () => {
    if (activeSection === 'resume') {
      return (
        <FormattedResumeSummary 
          summaryText={student.resume_summary || student.resume_text} 
          title="AI Resume Analysis & Competency Breakdown" 
        />
      );
    }
    if (activeSection === 'courses') {
      if (loadingCourses) {
        return <div style={{ color: 'var(--text-muted)', padding: '38px', textAlign: 'center' }}>Loading courses...</div>;
      }
      if (coursesData.length === 0) {
        return <div style={{ color: 'var(--text-faint)', padding: '38px', textAlign: 'center', border: '1px solid var(--border)', borderRadius: '10px' }}>No courses assigned yet.</div>;
      }
      return (
        <div style={{ display: 'grid', gap: '20px' }}>
          {coursesData.map((item, idx) => (
            <div key={idx} style={{ border: '1px solid var(--border)', borderRadius: '12px', background: 'var(--bg-body)', padding: '20px' }}>
              <h4 style={{ margin: '0 0 12px 0', color: 'var(--proto-white)', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <BookOpen size={18} style={{ color: 'var(--cyan)' }} />
                {item.selection.course?.title || 'Unknown Course'}
              </h4>
              <div style={{ marginBottom: '16px', display: 'flex', gap: '16px', fontSize: '13px', color: 'var(--text-muted)' }}>
                <span><strong style={{ color: 'var(--proto-white)' }}>Category:</strong> {item.selection.course?.category || '--'}</span>
                <span><strong style={{ color: 'var(--proto-white)' }}>Faculty:</strong> {item.selection.faculty_name || 'Not assigned'}</span>
              </div>
              {item.marks && item.marks.length > 0 ? (
                <table className="uni-table" style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8px' }}>
                  <thead>
                    <tr>
                      <th style={{ ...tableHeadStyle, padding: '10px 16px' }}>Test</th>
                      {item.marks.map((m, midx) => (
                        <th key={`th-${midx}`} style={{ ...tableHeadStyle, padding: '10px 16px' }}>{m.test_name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 800, fontSize: '12px', textTransform: 'uppercase' }}>Marks</td>
                      {item.marks.map((m, midx) => (
                        <td key={`td-${midx}`} style={{ padding: '12px 16px', color: 'var(--cyan)', fontWeight: 700 }}>
                          {m.marks_obtained} <span style={{ color: 'var(--text-muted)', fontSize: '0.9em', fontWeight: 500 }}>/ {m.max_marks}</span>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              ) : (
                <div style={{ color: 'var(--text-faint)', fontSize: '13px', padding: '12px', border: '1px dashed var(--border)', borderRadius: '8px', textAlign: 'center' }}>
                  No test marks recorded for this course yet.
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }
    if (activeSection === 'skillset') return renderScoreTable(student, skillset, 'SKILL CATEGORY', 'No skillset data submitted yet.', true);
    if (activeSection === 'toolset') return renderScoreTable(student, toolset, 'TOOLS / FRAMEWORKS', 'No toolset data submitted yet.', false);
    if (activeSection === 'projects') {
      return renderCards(projects, {
        icon: <Github size={18} />,
        empty: 'No projects added yet.',
        title: item => getText(item, ['name', 'title', 'repo', 'repository'], 'Project'),
        subtitle: item => getText(item, ['language', 'tech_stack', 'source'], ''),
        description: item => getText(item, ['description', 'summary'], ''),
        meta: item => item?.url || item?.html_url || item?.repo_url || item?.date || item?.createdAt || ''
      });
    }
    if (activeSection === 'experience') {
      return renderCards(experiences, {
        icon: <Briefcase size={18} />,
        empty: 'No experience added yet.',
        title: item => getText(item, ['role', 'title', 'position'], 'Experience'),
        subtitle: item => [item?.company, item?.organization, item?.org].filter(Boolean).join(' - '),
        description: item => getText(item, ['description', 'summary', 'desc'], ''),
        meta: item => [item?.duration, item?.start_date, item?.end_date, item?.period].filter(Boolean).join(' - ')
      });
    }
    if (activeSection === 'awards') {
      return renderCards(awards, {
        icon: <Award size={18} />,
        empty: 'No awards or certificates added yet.',
        title: item => getText(item, ['title', 'name', 'certificate_name'], 'Award'),
        subtitle: item => getText(item, ['issuer', 'organization', 'institute'], ''),
        description: item => getText(item, ['description', 'details', 'category'], ''),
        meta: item => [item?.issued_date, item?.date, item?.type, item?.issueDate, item?.credentialId ? `ID: ${item.credentialId}` : null].filter(Boolean).join(' - ')
      });
    }
    if (activeSection === 'marks') {
      return renderCards(marksCards, {
        icon: <FileText size={18} />,
        empty: 'No marks cards uploaded yet.',
        title: item => item.semester ? `Semester ${item.semester}` : 'Marks Card',
        subtitle: item => item.sgpa ? `SGPA: ${item.sgpa}` : '',
        description: item => '',
        meta: item => {
          const fileUrl = item?.file_url || item?.url || item?.link;
          return fileUrl ? <a href={getFileUrl(fileUrl)} target="_blank" rel="noreferrer" style={{ color: 'var(--cyan)', textDecoration: 'none' }}>View Document</a> : '';
        }
      });
    }
    if (activeSection === 'events') {
      return renderCards(events, {
        icon: <FileText size={18} />,
        empty: 'No events added yet.',
        title: item => getText(item, ['name', 'title', 'event_name'], 'Event'),
        subtitle: item => getText(item, ['type', 'category', 'tag'], ''),
        description: item => getText(item, ['description', 'details'], ''),
        meta: item => [item?.date, item?.status, item?.result].filter(Boolean).join(' - ')
      });
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
          <InfoPanel title="Academic Details" icon={<GraduationCap size={18} />}>
          <InfoRow label="USN" value={student.usn || 'Not assigned'} />
          <InfoRow label="Department" value={student.branch || student.department || student.degree || 'Not specified'} />
          <InfoRow label="Batch" value={student.academic_batch || student.graduation_year || 'Unknown'} />
          <InfoRow label="Program" value={[student.graduation_level, student.program].filter(Boolean).join(' - ') || 'Not specified'} />
          <InfoRow label="Admission" value={student.admission_number || student.year_of_admission || 'Not specified'} />
          <InfoRow label="Semester" value={student.admission_semester ? `Semester ${student.admission_semester}` : 'Not specified'} />
          <InfoRow label="Academic Year" value={student.academic_year || 'Not specified'} />
        </InfoPanel>
        
        <InfoPanel title="Platform Insights" icon={<Activity size={18} />}>
          <InfoRow label="AI Agent Usage" value={`${student.ai_agent_usage_count || 0} times`} />
          <InfoRow label="Last Agent Usage" value={student.last_ai_agent_usage_date ? new Date(student.last_ai_agent_usage_date).toLocaleDateString() : 'Never'} />
          <InfoRow label="Cross-Domain Allowed" value={student.allow_cross_domain_exploration ? 'Yes' : 'No'} />
          <InfoRow label="Current Assessment" value={student.last_assessment_score ?? 'Not taken'} />
          <InfoRow label="Previous Assessment" value={student.previous_assessment_score ?? 'Not taken'} />
          <InfoRow label="Total Attempts" value={parseJsonSafe(student.assessment_attempts_history).length || 0} />
          <InfoRow label="Marks Cards" value={`${marksCards.length} uploaded`} />
        </InfoPanel>

        <InfoPanel title="Domains & Skills" icon={<BookOpen size={18} />}>
          <div style={{ marginBottom: '18px' }}>
            <div style={smallLabelStyle}>Preferred Domains</div>
            {renderChips(domains)}
          </div>
          <div>
            <div style={smallLabelStyle}>Core Skills</div>
            {renderChips(skills)}
          </div>
        </InfoPanel>

        <InfoPanel title="Portfolio Snapshot" icon={<Github size={18} />}>
          <InfoRow label="GitHub" value={student.github_profile ? `@${student.github_profile}` : 'Not connected'} />
          <InfoRow label="Projects" value={`${projects.length} Total`} />
          <InfoRow label="Awards & Certs" value={`${awards.length} Total`} />
          <InfoRow label="Events & Activities" value={`${events.length} Total`} />
          <InfoRow label="Experience" value={`${experiences.length} Roles`} />
        </InfoPanel>
      </div>
      </div>
    );
  };

  return (
    <div className="unidash-fade-in">
      {onBack && (
        <button
          type="button"
          className="uni-btn-secondary"
          onClick={onBack}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '24px', padding: '10px 18px', borderRadius: '10px', fontWeight: 600 }}
        >
          <ArrowLeft size={16} /> Back
        </button>
      )}

      <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: '2.2rem', color: 'var(--proto-white)', margin: 0, fontWeight: 800 }}>Student Overview</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '1.15rem', margin: '12px 0 0 0', maxWidth: '600px' }}>Comprehensive profile, academic performance, and platform activity details.</p>
        </div>
        {canEdit && typeof onEditProfile === 'function' && (
          <button
            type="button"
            className="uni-btn-primary"
            onClick={() => onEditProfile(student)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 20px', borderRadius: '10px', fontWeight: 700, border: 'none', cursor: 'pointer', flexShrink: 0 }}
          >
            <Pencil size={16} /> Edit Profile
          </button>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '320px minmax(0, 1fr)', gap: '24px', alignItems: 'start' }}>
        <aside className="uni-card" style={{ padding: '28px', position: 'sticky', top: '24px', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
          <div style={{ display: 'grid', justifyItems: 'center', textAlign: 'center', gap: '16px' }}>
            <div style={{ width: '96px', height: '96px', borderRadius: '24px', background: 'linear-gradient(135deg, #0284c7 0%, #1e40af 100%)', color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 900, fontSize: '38px', boxShadow: '0 8px 16px rgba(2, 132, 199, 0.2)' }}>
              {(student.full_name || 'S').charAt(0)}
            </div>
            <div>
              <h3 style={{ color: 'var(--proto-white)', margin: 0, fontSize: '24px', fontWeight: 800 }}>{student.full_name || 'Unknown Student'}</h3>
              <p style={{ color: 'var(--cyan)', margin: '8px 0 0', fontWeight: 700 }}>{student.branch || student.department || student.degree || 'Department not specified'}</p>
              {student.gender && student.student_date_of_birth && (
                <p style={{ color: 'var(--text-faint)', margin: '6px 0 0', fontSize: '13px' }}>
                  {student.gender.charAt(0).toUpperCase() + student.gender.slice(1)} • {student.student_date_of_birth}
                </p>
              )}
            </div>
          </div>

          <div style={{ borderTop: '1px solid var(--border)', marginTop: '24px', paddingTop: '20px', display: 'grid', gap: '14px' }}>
            <span style={sidebarLineStyle}><Mail size={15} style={{ color: 'var(--cyan)' }} /> {student.email || 'No email'}</span>
            <span style={sidebarLineStyle}><Phone size={15} style={{ color: 'var(--cyan)' }} /> {student.phone_number || student.student_mobile_number || student.mobile_number || student.phone || 'No phone'}</span>
            <span style={sidebarLineStyle}><GraduationCap size={15} style={{ color: 'var(--cyan)' }} /> {student.usn || 'No USN'}</span>
            {student?.resume_url && (
              <button
                onClick={async (e) => {
                  e.preventDefault();
                  const url = student.resume_url;
                  if (url.startsWith('http')) {
                    window.open(url, '_blank');
                    return;
                  }
                  try {
                    const cleanPath = url.replace('/api/documents/', '').split('/').filter(Boolean).join('/');
                    const res = await api.get(`/api/documents/${cleanPath}`, { responseType: 'blob' });
                    const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                    const a = document.createElement('a');
                    a.href = blobUrl;
                    a.target = '_blank';
                    a.click();
                    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                  } catch (error) {
                    console.error('Error fetching resume:', error);
                    alert('Failed to load resume document.');
                  }
                }}
                className="uni-btn-primary"
                style={{ border: 'none', cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'none', textAlign: 'center', padding: '12px', borderRadius: '10px', marginTop: '10px', fontWeight: 700, display: 'flex', justifyContent: 'center', gap: '8px', width: '100%' }}
              >
                <FileText size={16} /> View Resume
              </button>
            )}
          </div>

          <div style={{ borderTop: '1px solid var(--border)', marginTop: '24px', paddingTop: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Stat value={skills.length} label="Skills" />
            <Stat value={toolset.length} label="Tools" />
            <Stat value={projects.length} label="Projects" />
            <Stat value={awards.length} label="Awards" />
          </div>
        </aside>

        <div className="uni-card" style={{ padding: 0, overflow: 'hidden', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
          <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', borderBottom: '1px solid var(--border)', padding: '12px 24px 0', background: 'rgba(0,0,0,0.01)' }}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveSection(tab.id)}
                style={{
                  border: 0,
                  background: 'transparent',
                  color: activeSection === tab.id ? 'var(--cyan)' : 'var(--text-muted)',
                  borderBottom: activeSection === tab.id ? '3px solid var(--cyan)' : '3px solid transparent',
                  padding: '12px 14px 14px',
                  fontWeight: activeSection === tab.id ? 800 : 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  fontSize: '15px',
                  transition: 'all 0.2s ease'
                }}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>
          <div style={{ padding: '28px' }}>{renderSection()}</div>
        </div>
      </div>
    </div>
  );
};

export default StudentProfileDetail;
